import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import {
  CreditCard,
  RefreshCw,
  Download,
  Plus,
  Trash2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Shield,
  Clock,
  Info,
  Copy,
  ExternalLink
} from 'lucide-react'

export default function StripePage() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  
  // Per aggiunta IP manuale
  const [newIP, setNewIP] = useState('')
  const [addingIP, setAddingIP] = useState(false)

  useEffect(() => {
    loadStatus()
  }, [])

  const loadStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminApi.getStripeIPStatus()
      setStatus(response.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleCheck = async () => {
    try {
      setChecking(true)
      setError(null)
      setSuccess(null)
      const response = await adminApi.checkStripeIPs()
      setStatus(prev => ({
        ...prev,
        ...response.data.data
      }))
      
      if (response.data.data.match) {
        setSuccess('✅ IP sincronizzati con Stripe')
      } else {
        setSuccess(`⚠️ Trovate ${response.data.data.missingIPs?.length || 0} differenze`)
      }
      setTimeout(() => setSuccess(null), 5000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante la verifica')
    } finally {
      setChecking(false)
    }
  }

  const handleSync = async () => {
    if (!confirm('Vuoi sincronizzare automaticamente gli IP con Stripe?\n\nQuesta operazione aggiornerà la lista IP e invierà una notifica email.')) {
      return
    }
    
    try {
      setSyncing(true)
      setError(null)
      setSuccess(null)
      const response = await adminApi.syncStripeIPs()
      
      // Ricarica status
      await loadStatus()
      
      const result = response.data.data
      if (result.hasChanges) {
        setSuccess(`✅ Sincronizzazione completata! Aggiunti: ${result.addedIPs?.length || 0}, Rimossi: ${result.removedIPs?.length || 0}`)
      } else {
        setSuccess('✅ Nessuna modifica necessaria - IP già sincronizzati')
      }
      setTimeout(() => setSuccess(null), 5000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante la sincronizzazione')
    } finally {
      setSyncing(false)
    }
  }

  const handleAddIP = async () => {
    if (!newIP.trim()) return
    
    // Validazione base IP
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/
    if (!ipRegex.test(newIP.trim())) {
      setError('Formato IP non valido (es: 192.168.1.1)')
      return
    }
    
    try {
      setAddingIP(true)
      setError(null)
      await adminApi.addStripeIP(newIP.trim())
      setNewIP('')
      await loadStatus()
      setSuccess('✅ IP aggiunto')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'aggiunta dell\'IP')
    } finally {
      setAddingIP(false)
    }
  }

  const handleRemoveIP = async (ip) => {
    if (!confirm(`Rimuovere l'IP ${ip}?\n\n⚠️ Attenzione: rimuovere un IP Stripe valido potrebbe bloccare i webhook!`)) {
      return
    }
    
    try {
      setError(null)
      await adminApi.removeStripeIP(ip)
      await loadStatus()
      setSuccess('✅ IP rimosso')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella rimozione dell\'IP')
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setSuccess('📋 Copiato negli appunti')
    setTimeout(() => setSuccess(null), 2000)
  }

  const formatDate = (date) => {
    if (!date) return 'Mai'
    return new Date(date).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const StatusBadge = ({ result }) => {
    if (result === 'match') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-4 w-4 mr-1" />
          Sincronizzato
        </span>
      )
    }
    if (result === 'mismatch') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-4 w-4 mr-1" />
          Differenze rilevate
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
        <Clock className="h-4 w-4 mr-1" />
        Mai verificato
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CreditCard className="h-7 w-7 mr-2 text-primary-600" />
            Stripe - Sicurezza Webhook
          </h1>
          <p className="text-gray-500 mt-1">
            Gestisci gli IP autorizzati per i webhook Stripe
          </p>
        </div>
        <a 
          href="https://stripe.com/docs/ips" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
        >
          Documentazione Stripe
          <ExternalLink className="h-4 w-4 ml-1" />
        </a>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <XCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-700 font-medium">Errore</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Shield className="h-5 w-5 text-primary-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Stato Configurazione</h2>
          </div>
          <StatusBadge result={status?.lastCheckResult} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">IP Configurati</p>
            <p className="text-2xl font-bold text-gray-900">{status?.ipCount || 0}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">Ultima Verifica</p>
            <p className="text-lg font-medium text-gray-900">{formatDate(status?.lastChecked)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-1">Ultimo Sync da Stripe</p>
            <p className="text-lg font-medium text-gray-900">{formatDate(status?.lastSyncedFromStripe)}</p>
          </div>
        </div>

        {/* Differenze se presenti */}
        {status?.lastCheckResult === 'mismatch' && (
          <div className="mb-6 space-y-3">
            {status?.lastCheckMissingIPs?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 font-medium mb-2">
                  ⚠️ IP mancanti (presenti in Stripe, non configurati):
                </p>
                <div className="flex flex-wrap gap-2">
                  {status.lastCheckMissingIPs.map(ip => (
                    <code key={ip} className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm">
                      + {ip}
                    </code>
                  ))}
                </div>
              </div>
            )}
            {status?.lastCheckExtraIPs?.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-orange-800 font-medium mb-2">
                  ⚠️ IP extra (configurati, non più in Stripe):
                </p>
                <div className="flex flex-wrap gap-2">
                  {status.lastCheckExtraIPs.map(ip => (
                    <code key={ip} className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
                      - {ip}
                    </code>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleCheck}
            disabled={checking}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center disabled:opacity-50"
          >
            {checking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Verifica Ora
          </button>
          
          <button
            onClick={handleSync}
            disabled={syncing}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center disabled:opacity-50"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Sincronizza da Stripe
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
          <Info className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Verifica automatica attiva</p>
            <p>
              Il sistema verifica automaticamente gli IP ogni giorno alle 08:00. 
              Se vengono rilevate differenze, gli IP vengono sincronizzati automaticamente 
              e viene inviata un'email a <strong>admin@ildiariodibordo.it</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* IP List */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">IP Configurati</h2>
          <button
            onClick={() => copyToClipboard(status?.ips?.join('\n') || '')}
            className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
          >
            <Copy className="h-4 w-4 mr-1" />
            Copia tutti
          </button>
        </div>

        {/* Add IP Form */}
        <div className="flex gap-3 mb-6">
          <input
            type="text"
            value={newIP}
            onChange={(e) => setNewIP(e.target.value)}
            placeholder="Aggiungi IP (es: 52.15.183.38)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            onKeyDown={(e) => e.key === 'Enter' && handleAddIP()}
          />
          <button
            onClick={handleAddIP}
            disabled={addingIP || !newIP.trim()}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
          >
            {addingIP ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Aggiungi
          </button>
        </div>

        {/* IP Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {status?.ips?.map((ip, index) => (
            <div 
              key={ip} 
              className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 group hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center">
                <span className="text-gray-400 text-sm mr-3 w-6">{index + 1}.</span>
                <code className="text-gray-700 font-mono">{ip}</code>
              </div>
              <button
                onClick={() => handleRemoveIP(ip)}
                className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Rimuovi IP"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {status?.usingFallback && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-700 text-sm">
              ⚠️ Stai usando gli IP di fallback. Clicca "Sincronizza da Stripe" per aggiornare.
            </p>
          </div>
        )}
      </div>

      {/* Documentation Reference */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-3">Riferimenti</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>
            <strong>Endpoint JSON Stripe:</strong>{' '}
            <a 
              href="https://stripe.com/files/ips/ips_webhooks.json" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              https://stripe.com/files/ips/ips_webhooks.json
            </a>
          </p>
          <p>
            <strong>Documentazione:</strong>{' '}
            <a 
              href="https://stripe.com/docs/ips" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary-600 hover:underline"
            >
              https://stripe.com/docs/ips
            </a>
          </p>
          <p className="text-gray-500 mt-3">
            Gli IP webhook di Stripe possono cambiare periodicamente. 
            Il sistema verifica automaticamente ogni giorno e sincronizza se necessario.
          </p>
        </div>
      </div>
    </div>
  )
}
